/** 
 * Nosto-OptionsForYou.js
 *
 * Carrusel "Opciones para ti" de la página de producto.
 *
 * Flujo:
 * 0. Si existen productos en product.metafields.custom.completa_tu_look
 *    (misma regla que "Completa tu look"), se renderizan esos productos
 *    y no se consulta Nosto.
 * 1. <nosto-recommendations> solicita el placement.
 * 2. Este componente recibe "nosto:recommendations:loaded".
 * 3. Cada producto se renderiza con la vista "product-card"
 *    mediante NostoProductViewService.
 * 4. ProductCard.js inicializa wishlist, CTA y quick buy.
 *
 * Si no hay productos, el bloque se oculta.
 */
(() => {
    "use strict";

    class NostoOptionsForYou extends HTMLElement {
        constructor() {
            super();

            this.isRendered = false;
            this.isRendering = false;
            this.destroyed = false;

            this.handleLoaded = this.handleLoaded.bind(this);
            this.handleError = this.handleError.bind(this);
        }

        connectedCallback() {
            this.destroyed = false;

            this.track = this.querySelector(
                "[data-nosto-options-for-you-track]"
            );

            this.recommendations = this.querySelector(
                "nosto-recommendations"
            );

            this.manualProductsElement = this.querySelector(
                "[data-options-for-you-manual-products]"
            );

            if (
                !this.track ||
                (!this.recommendations && !this.manualProductsElement)
            ) {
                this.hideBlock();
                return;
            }

            this.productViewService =
                this.getProductViewService();

            this.initializeQuickBuyModal();

            if (this.manualProductsElement) {
                this.initializeManualProducts();
                return;
            }

            this.addEventListener(
                "nosto:recommendations:loaded",
                this.handleLoaded
            );

            this.addEventListener(
                "nosto:recommendations:error",
                this.handleError
            );

            /*
             * La respuesta de Nosto pudo llegar antes de que
             * este componente se definiera.
             */
            if (this.recommendations.lastResult) {
                this.renderFromDetail(
                    this.recommendations.lastResult
                );
            }
        }

        disconnectedCallback() {
            this.destroyed = true;

            this.removeEventListener(
                "nosto:recommendations:loaded",
                this.handleLoaded
            );

            this.removeEventListener(
                "nosto:recommendations:error",
                this.handleError
            );
        }

        /* ==========================================================================
           Services
           ========================================================================== */

        getProductViewService() {
            if (window.NOSTO_PRODUCT_VIEW_SERVICE) {
                return window.NOSTO_PRODUCT_VIEW_SERVICE;
            }

            if (typeof window.NostoProductViewService !== "function") {
                console.error(
                    "[NostoOptionsForYou] NostoProductViewService no está disponible."
                );

                return null;
            }

            window.NOSTO_PRODUCT_VIEW_SERVICE =
                new window.NostoProductViewService({
                    cardView: "product-card",
                    quickBuyView: "nosto-quick-buy",
                    concurrency: 4,
                    cache: true,
                    debug: window.NOSTO_DEBUG === true,
                });

            return window.NOSTO_PRODUCT_VIEW_SERVICE;
        }

        initializeQuickBuyModal() {
            if (window.NOSTO_QUICK_BUY_MODAL?.isInitialized) {
                return;
            }

            const modalElement = document.querySelector(
                "[data-nosto-quick-buy-modal]"
            );

            if (
                typeof window.NostoQuickBuyModal !== "function" ||
                !modalElement
            ) {
                return;
            }

            try {
                const controller = new window.NostoQuickBuyModal({
                    modal: modalElement,
                    viewService: window.NOSTO_PRODUCT_VIEW_SERVICE,
                    debug: window.NOSTO_DEBUG === true,
                });

                controller.init();

                window.NOSTO_QUICK_BUY_MODAL = controller;
            } catch (error) {
                console.error(
                    "[NostoOptionsForYou] No fue posible inicializar el quick buy.",
                    error
                );
            }
        }

        /* ==========================================================================
           Events
           ========================================================================== */

        handleLoaded(event) {
            if (event.target !== this.recommendations) {
                return;
            }

            this.renderFromDetail(event.detail);
        }

        handleError(event) {
            if (event.target !== this.recommendations) {
                return;
            }

            this.hideBlock();
        }

        /* ==========================================================================
           Rendering
           ========================================================================== */

        /**
         * Inicializa productos definidos manualmente mediante:
         *
         * product.metafields.custom.completa_tu_look
         */
        initializeManualProducts() {
            let products = [];

            try {
                products = JSON.parse(
                    this.manualProductsElement.textContent || "[]"
                );
            } catch (error) {
                console.error(
                    "[NostoOptionsForYou] No fue posible leer custom.completa_tu_look.",
                    error
                );

                this.hideBlock();
                return;
            }

            /*
             * Los productos del metafield no tienen placementId,
             * resultId ni divId porque no provienen de Nosto.
             */
            this.renderProducts(
                this.filterProducts(Array.isArray(products) ? products : []),
                {
                    source: "metafield",
                    placementId: "",
                    resultId: "",
                    divId: "",
                }
            );
        }

        renderFromDetail(detail) {
            if (!detail) {
                return;
            }

            this.renderProducts(this.getProducts(detail), {
                source: "nosto",
                placementId: String(detail.placementId || "").trim(),
                resultId: String(detail.resultId || "").trim(),
                divId: String(detail.divId || "").trim(),
            });
        }

        async renderProducts(products, context) {
            if (this.isRendered || this.isRendering) {
                return;
            }

            if (!products.length || !this.productViewService) {
                this.hideBlock();
                return;
            }

            this.isRendering = true;

            this.recommendationContext = context;

            this.track
                .querySelectorAll("[data-nosto-options-for-you-skeleton]")
                .forEach((skeleton) => skeleton.remove());

            /*
             * Creamos los slots primero para conservar el orden
             * de Nosto aunque las peticiones terminen en desorden.
             */
            const items = products.map((product) => {
                const item = document.createElement("div");

                item.className =
                    "nosto-options-for-you__item nosto-options-for-you__item--loading";
                item.setAttribute("role", "listitem");
                item.dataset.productId = product.productId;
                item.dataset.productHandle = product.handle;
                item.innerHTML = `
                    <div class="nosto-options-for-you__skeleton-media"></div>
                    <div class="nosto-options-for-you__skeleton-line"></div>
                    <div class="nosto-options-for-you__skeleton-line nosto-options-for-you__skeleton-line--short"></div>
                `;

                this.track.appendChild(item);

                return { product, item };
            });

            await Promise.all(
                items.map(({ product, item }) =>
                    this.renderProduct(product, item)
                )
            );

            this.isRendering = false;

            if (this.destroyed) {
                return;
            }

            if (!this.track.querySelector("[data-product-card]")) {
                this.hideBlock();
                return;
            }

            this.isRendered = true;
            this.dataset.loaded = "true";
        }

        async renderProduct(product, item) {
            try {
                const context = this.recommendationContext;
                const isNosto = context.source === "nosto";

                const html = await this.productViewService.getCard({
                    handle: product.handle,
                    url: product.url,
                    productId: product.productId,
                    view: "product-card",
                    context: {
                        source: isNosto
                            ? "pdp-options-for-you"
                            : "pdp-options-for-you-metafield",
                        /*
                         * Solo productos provenientes
                         * de Nosto necesitan attribution.
                         */
                        track: isNosto ? "recommendation" : "",
                        attributionKey: context.resultId,
                        placementId: context.placementId,
                        resultId: context.resultId,
                        divId: context.divId,
                    },
                });

                if (this.destroyed || !item.isConnected) {
                    return;
                }

                const template = document.createElement("template");
                template.innerHTML = html.trim();

                const cardRoot = template.content.querySelector(
                    "[data-product-card]"
                );

                if (!cardRoot) {
                    throw new Error(
                        'La vista "product-card" no devolvió [data-product-card].'
                    );
                }

                cardRoot.dataset.productSource = isNosto
                    ? "nosto-recommendation"
                    : "metafield";
                cardRoot.dataset.nostoPlacementId = context.placementId;
                cardRoot.dataset.nostoResultId = context.resultId;
                cardRoot.dataset.nostoDivId = context.divId;
                cardRoot.dataset.nostoAttributionKey = context.resultId;

                item.replaceChildren(cardRoot);
                item.classList.remove(
                    "nosto-options-for-you__item--loading"
                );

                if (typeof window.initializeProductCards === "function") {
                    window.initializeProductCards(item);
                }
            } catch (error) {
                console.error(
                    "[NostoOptionsForYou] No se pudo renderizar el producto.",
                    { product, error }
                );

                item.remove();
            }
        }

        hideBlock() {
            this.hidden = true;

            this.closest(".nosto-options-for-you-spacing")
                ?.setAttribute("hidden", "");
        }

        /* ==========================================================================
           Product normalization
           ========================================================================== */

        getProducts(detail) {
            const source =
                detail.recommendedProducts ??
                detail.allRecommendedProducts ??
                detail.products ??
                [];

            if (!Array.isArray(source)) {
                return [];
            }

            return this.filterProducts(source);
        }

        filterProducts(source) {
            const currentProductId = String(
                this.recommendations?.dataset.productId ||
                this.dataset.productId ||
                ""
            );

            const seen = new Set();

            return source
                .map((product) => this.normalizeProduct(product))
                .filter((product) => {
                    if (!product) {
                        return false;
                    }

                    if (
                        currentProductId &&
                        product.productId === currentProductId
                    ) {
                        return false;
                    }

                    const key = product.handle || product.productId;

                    if (seen.has(key)) {
                        return false;
                    }

                    seen.add(key);

                    return true;
                });
        }

        normalizeProduct(product) {
            if (!product) {
                return null;
            }

            if (typeof product === "string") {
                const value = product.trim();

                return value
                    ? { productId: value, handle: "", url: "" }
                    : null;
            }

            const productId = String(
                product.productId ?? product.product_id ?? product.id ?? ""
            ).trim();

            const url = String(
                product.url ?? product.productUrl ?? product.product_url ?? ""
            ).trim();

            const handle =
                String(
                    product.handle ??
                    product.productHandle ??
                    product.product_handle ??
                    ""
                ).trim() || this.getHandleFromUrl(url);

            if (!productId && !handle) {
                return null;
            }

            return { productId, handle, url };
        }

        getHandleFromUrl(url) {
            if (!url) {
                return "";
            }

            try {
                const { pathname } = new URL(url, window.location.origin);

                return pathname.match(/\/products\/([^/?#]+)/)?.[1] || "";
            } catch (error) {
                return "";
            }
        }
    }

    if (!customElements.get("nosto-options-for-you")) {
        customElements.define(
            "nosto-options-for-you",
            NostoOptionsForYou
        );
    }
})();
